import { ReactComponent as Court } from "./assets/volleyball-court.svg";
import * as service from "../../service";
import React, {
  FC,
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { Overlay } from "../../components/Overlay";
import { LANGUAGE } from "_constants";
import { ICONS, MESSAGES, getVolleyballCodeMeta } from "./constants";
import { VolleyballStatusRenderer } from "./components/StatusRenderer";
import AdDisplay from "components/common/AdDisplay";
import { Carousel } from "react-responsive-carousel";
import AdBannerSvg from "./assets/ad-banner.svg";
import GeniusFeeds from "../../assets/ads/genius-logo.svg";
import FastGamesApi from "../../assets/ads/fastgamesapi.svg";
import Monetria from "../../assets/ads/monetria.svg";
import {
  VOLLEYBALL_TABS,
  isPreMatch as isPreMatchHelper,
  VolleyballTabKey,
} from "./utils";
import { SIDE } from "_constants";
import { ServiceAnimation } from "./indicators/ServiceAnimation";
import { LastSixPoints } from "./indicators/LastSixPoints";
import { VolleyballScoreBoard } from "./components/ScoreBoard";
import { ServePointsWon } from "./indicators/ServePointsWon";
import { CurrentStreak } from "./indicators/CurrentStreak";
import { Stats } from "./statistics";
import PreMatchIntro from "../../components/PreMatchIntro";
import VolleyballMatchTimeline, {
  buildVolleyballMatchTimelineEvents,
} from "./timeline";
import { SportTabs } from "components/common/SportTabs";
import VolleyballArena from "./assets/volleyball-bg.png";
import {
  AdBanner,
  FlatLayer,
  FullHeightTabPanel,
  createBackgroundComponent,
} from "../../components/common";

export type VolleyProps = {
  status: service.MatchStatus;
  language?: LANGUAGE;
};

const StyledCourtVolleyball = createBackgroundComponent({
  backgroundImage: VolleyballArena,
  baseClassName:
    "w-full h-full bg-center bg-cover flex items-center justify-center relative overflow-hidden",
  displayName: "StyledCourtVolleyball",
});

export const Volleyball: FC<VolleyProps> = memo(
  ({ status, language = LANGUAGE.ENGLISH }) => {
    const [activeTab, setActiveTab] = useState<VolleyballTabKey>("narration");
    const isPreMatch = useMemo(() => isPreMatchHelper(status), [status]);
    const messageGenerator = useCallback(
      ({
        language,
        normalizedStatusCode,
      }: {
        language: LANGUAGE;
        normalizedStatusCode?: string;
      }) => {
        const langDict = MESSAGES[language];
        if (!langDict) return undefined;

        const meta = getVolleyballCodeMeta(status, normalizedStatusCode);
        const lookupKey = meta.lookupCodes.find((code) => langDict[code]);

        const message = lookupKey ? langDict[lookupKey] : undefined;

        let icon = lookupKey ? ICONS[lookupKey] : undefined;

        if (!icon) {
          for (const key of meta.lookupCodes) {
            const candidate = ICONS[key];
            if (candidate) {
              icon = candidate;
              break;
            }
          }
        }

        const resolvedMessage = message ?? "";

        if (!resolvedMessage && !icon) return undefined;

        return { message: resolvedMessage, icon };
      },
      [status]
    );

    const {
      normalizedCode: resolvedNormalizedCode,
      baseCode,
      side: codeSide,
    } = useMemo(() => getVolleyballCodeMeta(status), [status]);

    const derivedSide: SIDE =
      codeSide ??
      ((status as any)?.side === 2
        ? SIDE.AWAY
        : (status as any)?.side === 1
        ? SIDE.HOME
        : SIDE.HOME);

    const normalizedCodeBase = baseCode ?? resolvedNormalizedCode?.slice(-3);
    const isServeCode = normalizedCodeBase === "248";
    const isLastSixCode = normalizedCodeBase === "257";
    const isServePointsWonCode = normalizedCodeBase === "258";
    const isCurrentStreakCode = normalizedCodeBase === "259";

    const matchEvents = useMemo(
      () => buildVolleyballMatchTimelineEvents(status),
      [status]
    );

    const hasMatchTimeline = useMemo(() => {
      const raw = (status as any)?.m;
      if (!Array.isArray(raw)) return false;
      return raw.some((item: any) => {
        const tmValue = Number(
          typeof item?.tm === "string" || typeof item?.tm === "number"
            ? item.tm
            : NaN
        );
        const hasMinute = Number.isFinite(tmValue) && tmValue > 0;
        const tiValue = String(item?.ti ?? "").trim();
        const hasTeam = tiValue === "1" || tiValue === "2";
        return hasMinute || hasTeam;
      });
    }, [status]);

    const tabs = useMemo(
      () =>
        VOLLEYBALL_TABS.filter(
          (tab) => tab.key !== "match" || hasMatchTimeline
        ),
      [hasMatchTimeline]
    );

    useEffect(() => {
      if (!tabs.some((tab) => tab.key === activeTab) && tabs.length > 0) {
        setActiveTab(tabs[0].key);
      }
    }, [tabs, activeTab]);

    const selectedTabIndex = useMemo(() => {
      const idx = tabs.findIndex((tab) => tab.key === activeTab);
      return idx >= 0 ? idx : 0;
    }, [tabs, activeTab]);

    return (
      <>
        <SportTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as VolleyballTabKey)}
        />

        <Carousel
          showThumbs={false}
          selectedItem={selectedTabIndex}
          autoPlay={false}
          emulateTouch
          showStatus={false}
          showArrows={false}
          showIndicators={false}
          swipeable={false}
        >
          {tabs.map((tab) => {
            if (tab.key === "narration") {
              return (
                <StyledCourtVolleyball className="styled-court" key="narration">
                  <VolleyballScoreBoard status={status} />
                  <AdBanner src={AdBannerSvg} alt="Ad Banner" />
                  <AdDisplay adImages={[GeniusFeeds, FastGamesApi, Monetria]} />
                  <Overlay
                    status={status}
                    messageGenerator={messageGenerator}
                    language={language}
                    StatusRenderer={VolleyballStatusRenderer}
                  >
                    <Court />

                    {!isPreMatch && (
                      <>
                        {isServeCode && (
                          <ServiceAnimation
                            side={derivedSide}
                            className="absolute left-0 right-0 top-[25vw]"
                          />
                        )}
                      </>
                    )}
                  </Overlay>

                  <FlatLayer>
                    {isLastSixCode && (
                      <LastSixPoints statusText={status.sts} status={status} />
                    )}

                    {isServePointsWonCode && (
                      <ServePointsWon statusText={status.sts} status={status} />
                    )}

                    {isCurrentStreakCode && (
                      <CurrentStreak statusText={status.sts} status={status} />
                    )}
                  </FlatLayer>

                  {isPreMatch && (
                    <FlatLayer>
                      <PreMatchIntro status={status} />
                    </FlatLayer>
                  )}
                </StyledCourtVolleyball>
              );
            }

            if (tab.key === "stats") {
              return (
                <FullHeightTabPanel key="stats">
                  <Stats status={status} />
                </FullHeightTabPanel>
              );
            }

            if (tab.key === "match") {
              return (
                <FullHeightTabPanel key="match">
                  <VolleyballMatchTimeline
                    events={matchEvents}
                    status={status}
                  />
                </FullHeightTabPanel>
              );
            }

            return null;
          })}
        </Carousel>
      </>
    );
  }
);

export { StyledCourtVolleyball, Overlay };
